// This file configures the initialization of PostHog on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://posthog.com/docs/libraries/next-js

import type { BeforeSendFn } from 'posthog-js';
import posthog from 'posthog-js';
import { getClientAnalyticsPlatform } from './lib/analytics/platform';
import { redactPostHogExceptionEvent } from './lib/analytics/posthog-errors';

const isDevelopment =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1');

// ============================================================================
// PostHog Analytics
// ============================================================================

import { env } from './config/env';

const posthogKey = env.NEXT_PUBLIC_POSTHOG_KEY;
const posthogHost = env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com';
const appEnv = env.NEXT_PUBLIC_APP_ENV || 'local';

// Track initialization state for components to check
let posthogInitialized = false;

// One-time callbacks to run when PostHog finishes loading. Lets deferred
// producers (e.g. plugin updater-status capture) stash work and flush it once
// analytics is ready, instead of busy-polling `isPostHogInitialized()`.
const posthogReadyCallbacks: Array<() => void> = [];

/**
 * Runs `callback` once PostHog is ready to send events. Fires synchronously if
 * PostHog is already initialized. If PostHog is never configured (no key), the
 * callback is never invoked, which is correct: there is nothing to send.
 */
export function onPostHogReady(callback: () => void): void {
  if (posthogInitialized) {
    callback();
    return;
  }

  posthogReadyCallbacks.push(callback);
}

const beforeSendPostHogEvent: BeforeSendFn = (event) =>
  redactPostHogExceptionEvent(event, {
    environment: appEnv,
    platform: getClientAnalyticsPlatform(),
  });

if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    // Use modern defaults for pageview/pageleave tracking and error capture.
    defaults: '2026-01-30',
    // Enable session recording
    disable_session_recording: false,
    // Respect Do Not Track browser setting
    respect_dnt: true,
    // Mask all text in session recordings for privacy
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-mask]',
    },
    // Capture browser runtime failures explicitly while keeping console.error
    // capture disabled until volume and privacy impact are reviewed.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    error_tracking: {
      captureExtensionExceptions: false,
      exception_steps: {
        enabled: false,
      },
    },
    before_send: beforeSendPostHogEvent,
    // Debug mode in development
    loaded: (ph) => {
      const platform = getClientAnalyticsPlatform();

      // Register super properties - attached to ALL events
      ph.register({ environment: appEnv, platform });
      posthogInitialized = true;

      // Flush any work deferred until analytics was ready.
      for (const callback of posthogReadyCallbacks.splice(0)) {
        try {
          callback();
        } catch {
          // A misbehaving ready-callback must not break PostHog init.
        }
      }

      if (isDevelopment) {
        ph.debug();
        console.log('[POSTHOG] Initialized in debug mode');
      }
    },
  });
} else if (typeof window !== 'undefined' && isDevelopment) {
  console.log('[POSTHOG] Skipping - no API key configured');
}

/**
 * Check if PostHog has been initialized.
 * Use this before calling PostHog methods to prevent errors.
 */
export function isPostHogInitialized(): boolean {
  return posthogInitialized;
}
